import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchProductCategoriesDev, fetchProductsByCategory } from "../api/productApi";

export default function WomenPage() {
  const navigate = useNavigate();
  const [womenCategories, setWomenCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadWomenTypes = async () => {
      setLoading(true);
      setError(null);
      try {
        const { women } = await fetchProductCategoriesDev();
        setWomenCategories(women || []);
      } catch (err) {
        console.error("Failed to load women categories", err);
        setError(err.message || "Failed to load women categories");
        setWomenCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadWomenTypes();
  }, []);

  const handleSubcategoryClick = async (category, productTypeName) => {
    try {
      const products = await fetchProductsByCategory(category, productTypeName, false, "women");
      navigate("/products", {
        state: {
          products,
          category: category,
          subcategory: productTypeName,
        },
      });
    } catch (err) {
      console.error("Failed to load products", err);
      alert("Could not load products. Please try again.");
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
          Loading women's categories...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Error loading women's categories
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/")}
          startIcon={<ArrowBackIcon />}
        >
          Go Back Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Back Button and Title Side by Side */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Back
        </Button>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "text.primary",
          }}
        >
          Women
        </Typography>
      </Box>

      {/* Categories */}
      {womenCategories.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No categories available
          </Typography>
        </Box>
      ) : (
        <Box>
          {womenCategories.map((categoryItem, categoryIndex) => (
            <Box key={categoryIndex} sx={{ mb: 6 }}>
              {/* Category Header */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  {categoryItem.category}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.875rem" }}
                >
                  {categoryItem.total_count} {categoryItem.total_count === 1 ? "item" : "items"} total
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Subcategories Grid */}
              {categoryItem.subcategories && categoryItem.subcategories.length > 0 ? (
                <Grid container spacing={4}>
                  {categoryItem.subcategories.map((subcategory, subIndex) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={subIndex}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          borderRadius: 2,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                          },
                        }}
                        onClick={() =>
                          handleSubcategoryClick(
                            categoryItem.category,
                            subcategory.product_type_name
                          )
                        }
                      >
                        {/* Image */}
                        <CardMedia
                          component="img"
                          height="300"
                          image={
                            subcategory.image_url
                              ?.replace(
                                "storage.cloud.google.com",
                                "storage.googleapis.com"
                              ) ||
                            `https://picsum.photos/seed/${subcategory.product_type_name}/400/300`
                          }
                          alt={subcategory.product_type_name}
                          sx={{
                            objectFit: "cover",
                            backgroundColor: "grey.100",
                          }}
                          onError={(e) => {
                            e.target.src = `https://picsum.photos/seed/${subcategory.product_type_name}/400/300`;
                          }}
                        />

                        {/* Content */}
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            p: 2,
                          }}
                        >
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              textTransform: "capitalize",
                              color: "text.primary",
                            }}
                          >
                            {subcategory.product_type_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {subcategory.product_count}{" "}
                            {subcategory.product_count === 1 ? "item" : "items"}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No subcategories available for this category
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Total Count */}
      {womenCategories.length > 0 && (
        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Showing {womenCategories.length}{" "}
            {womenCategories.length === 1 ? "category" : "categories"}
          </Typography>
        </Box>
      )}
    </Container>
  );
}

